// src/pages/Login.tsx
import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../api/axiosInstance'; // ← our shared instance with request + response interceptors

const BackgroundPattern = styled.div`
    position: absolute;
    inset: 0;
    opacity: 0.05;
    background-image:
            radial-gradient(circle at 20% 30%, #990147 2px, transparent 0),
            radial-gradient(circle at 80% 70%, #990147 2px, transparent 0);
    background-size: 70px 70px;
    pointer-events: none;
    z-index: 0;
`;

const LoginSection = styled.section`
    position: relative;
    width: 100%;
    max-width: 480px;
    background: rgba(255, 255, 255, 0.97);
    border-radius: 24px;
    padding: 3.5rem 2.5rem;
    box-shadow: 0 25px 50px rgba(153, 1, 71, 0.18);
    backdrop-filter: blur(12px);
    border: 1px solid rgba(255, 255, 255, 0.9);
    overflow: hidden;
`;

const HeaderWrapper = styled.div`
    text-align: center;
    margin-bottom: 3rem;
    position: relative;
    z-index: 1;
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    gap: 1.8rem;
    width: 100%;
    position: relative;
    z-index: 1;
`;

const InputWrapper = styled.div`
    width: 100%;
    position: relative;
    z-index: 1;
`;

const Input = styled(motion.input)`
    width: 100%;
    padding: 1.3rem 1.8rem;
    border-radius: 16px;
    border: 2px solid rgba(153, 1, 71, 0.25);
    background: white;
    font-size: 1.1rem;
    color: #333;
    box-sizing: border-box;

    &::placeholder { color: #aaa; }

    &:focus {
        outline: none;
        border-color: #990147;
        box-shadow: 0 0 0 5px rgba(153, 1, 71, 0.15);
    }
`;

const SubmitButton = styled(motion.button)`
    width: 100%;
    padding: 1.4rem;
    border: none;
    border-radius: 16px;
    background: linear-gradient(135deg, #990147 0%, #cc3366 100%);
    color: white;
    font-size: 1.2rem;
    font-weight: 700;
    cursor: pointer;
    margin-top: 1rem;
    box-shadow: 0 12px 35px rgba(153, 1, 71, 0.3);
    transition: all 0.3s ease;
    position: relative;
    z-index: 1;

    &:hover:not(:disabled) {
        transform: translateY(-4px);
        box-shadow: 0 18px 45px rgba(153, 1, 71, 0.4);
    }

    &:disabled {
        opacity: 0.7;
        cursor: not-allowed;
    }
`;

const Login: React.FC = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);

    const { login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!username.trim() || !password) {
            return toast.error('Vyplňte všetky polia');
        }

        setLoading(true);

        try {
            const res = await api.post('/auth/login', { username, password });

            const token = res.data.jwt;

            if (!token) {
                throw new Error('Server did not return a JWT token');
            }

            login(token);
            toast.success('Úspešne prihlásený!');

            // Redirect to where user originally wanted to go (or /admin)
            const redirectTo = (location.state as any)?.from?.pathname || '/admin';
            navigate(redirectTo, { replace: true });
        } catch (err: any) {
            console.error('Login error:', err);

            const message =
                err.response?.data?.message ||
                err.response?.data?.error ||
                err.message ||
                'Nesprávne meno alebo heslo';

            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            style={{
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #fff 0%, #f5f5f5 50%, #c0c0c0 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '2rem',
            }}
        >
            <LoginSection>
                <BackgroundPattern />

                <HeaderWrapper>
                    <motion.h2
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7 }}
                        style={{
                            fontSize: '2.6rem',
                            fontWeight: 900,
                            background: 'linear-gradient(135deg, #333 0%, #990147 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                        }}
                    >
                        Admin Panel
                    </motion.h2>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                    >
                        Prihláste sa do administrácie
                    </motion.p>
                </HeaderWrapper>

                <Form onSubmit={handleSubmit}>
                    <InputWrapper>
                        <Input
                            type="text"
                            placeholder="Používateľské meno"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            required
                            disabled={loading}
                            autoFocus
                            whileTap={{ scale: 0.98 }}
                        />
                    </InputWrapper>

                    <InputWrapper>
                        <Input
                            type="password"
                            placeholder="Heslo"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            disabled={loading}
                            whileTap={{ scale: 0.98 }}
                        />
                    </InputWrapper>

                    <SubmitButton
                        type="submit"
                        disabled={loading}
                        whileHover={{ scale: loading ? 1 : 1.04 }}
                        whileTap={{ scale: loading ? 1 : 0.97 }}
                    >
                        {loading ? 'Prihlasujem...' : 'Prihlásiť sa'}
                    </SubmitButton>
                </Form>
            </LoginSection>
        </div>
    );
};

export default Login;